import { lazy } from 'react'
import type { ComponentType } from 'react'

// React.lazy, hardened against the "Failed to fetch dynamically imported module" error that a lazy
// chunk can throw — a transient network blip, or a stale hashed chunk 404ing after a new deploy
// replaced it. A single retry recovers from a momentary failure; a persistent one is rethrown so the
// surrounding ChunkErrorBoundary renders its inline fallback for that subtree alone.
//
// It deliberately does NOT call window.location.reload(): consumers provide a ChunkErrorBoundary
// fallback with a user-initiated reload when appropriate. Refreshing the entire page without a user
// action can discard unsaved work — users reported the
// surprise reload, with no action of their own, as a bug. Degrade the widget instead and let the
// fallback invite a manual reload.
// The <any> constraint must accept components with any props shape; T still infers the real props
// so callers stay type-checked.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- constraint must accept components
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory()
    } catch {
      // One retry absorbs a transient fetch failure without disturbing the rest of the page. If it
      // fails again the error propagates to the ChunkErrorBoundary, which shows the inline fallback.
      return await factory()
    }
  })
}
